import { HttpHandler } from "@/http/HttpHandler";
import { Account } from "@/entities/auth/Account";
import store from "@/store/index";

export class UserService {
  private static accountKey: string = "account";

  // --- LOGIN WITH USERNAME + PASSWORD ---
  static async login(email: string, password: string, rememberMe: boolean) {
    const httpHandler = new HttpHandler(store);
    const account: Account = await httpHandler.login(email, password);

    account.setExpirationDate(rememberMe);
    this.saveAccount(account, rememberMe);

    return account;
  }

  // --- LOGIN WITH PRIVATE KEY ---
  static async loginWithKey(privateKey: string, rememberMe: boolean) {
    const httpHandler = new HttpHandler(store);
    const account: Account = await httpHandler.loginWithKey(privateKey);

    account.setExpirationDate(rememberMe);
    this.saveAccount(account, rememberMe);

    return account;
  }

  // --- MANUAL SET ACCOUNT (used after signup etc.) ---
  static setAccount(account: Account, rememberMe: boolean = true): void {
    account.setExpirationDate(rememberMe);
    this.saveAccount(account, rememberMe);
  }

  // --- HELPER TO SAVE ACCOUNT ---
  private static saveAccount(account: Account, rememberMe: boolean): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(this.accountKey, JSON.stringify(account));
  }

  // --- GET ACCOUNT ---
  static getAccount(): Account | null {
    const data =
      localStorage.getItem(this.accountKey) ||
      sessionStorage.getItem(this.accountKey);

    if (!data) return null;

    const parsed = JSON.parse(data);
    const account = new Account(
      parsed.id,
      parsed.login,
      parsed.role,
      parsed.token,
      parsed.name,
      parsed.surname,
      parsed.fillSurvey ?? false
    );

    account.expirationDate = new Date(parsed.expirationDate);
    return account;
  }

  // --- LOGOUT ---
  static logout(): void {
    localStorage.removeItem(this.accountKey);
    sessionStorage.removeItem(this.accountKey);
  }

  // --- CHECK AUTHENTICATION ---
  static isAuthenticated(): boolean {
    return this.getAccount() !== null;
  }

  // --- PROLONG SESSION ---
  static prolongExpirationDate(): void {
    const account = this.getAccount();
    if (!account) return;

    account.prolongExpirationDate();

    // Save again based on original rememberMe (guess storage)
    const isPersistent = !!localStorage.getItem(this.accountKey);
    this.saveAccount(account, isPersistent);
  }

  // --- RESEND VERIFICATION ---
  static async resendVerification(accountId: number): Promise<any> {
    const httpHandler = new HttpHandler(store);
    return await httpHandler.resendVerification(accountId);
  }

  // --- SIGNUP ---
  static async signup(
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    company: string,
    referrer: number,
    password: string
  ): Promise<any> {
    const httpHandler = new HttpHandler(store);
    return await httpHandler.signup(
      firstName,
      lastName,
      email,
      phone,
      company,
      referrer,
      password
    );
  }
}
